import React, { useEffect, useState } from 'react';
import { VictoryChart, VictoryAxis, VictoryLegend, VictoryLine } from 'victory';
import dayjs from 'dayjs';
import abbreviateNumber from 'utils/abbreviate-number';
import utc from 'dayjs/plugin/utc';
import { getDataAtPointInTime } from 'utils/data-vis-helpers';
import TopSectionStats from '../chart-helpers/top-section-stats';
import ChartStatTableRow from '../chart-helpers/bottom-section-stats';
import { buildClassName } from 'utils/build-class-name';
import styles from 'components/charts/address-page-charts.module.scss';
import { soldPricelineStyle, listPricelineStyle, axisStyle, soldPricelineStyleExp, listPricelineStyleExp, axisStyleExp } from '../chart-styles';
import EmptyState from 'components/empty-state';
import { useThemeContext } from 'contexts';
import { ThemeNames } from 'types/themes';

import type { GraphData, Props } from '../types';

dayjs.extend(utc);

export default function ListVsSold({ listing, chartData, isBlurred }: Props) {
  const { themeName } = useThemeContext();
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;
  const [hasData, setHasData] = useState(true);
  const [cityData, setCityData] = useState([]);
  const [listPriceChartData, setListPriceChartData] = useState([]);
  const [cityAverageHomePrice, setCityAverageHomePrice] = useState('');
  const [oneMonthChange, setOneMonthChange] = useState(0);
  const [threeMonthChange, setThreeMonthChange] = useState(0);
  const [sixMonthChange, setSixMonthChange] = useState(0);

  const setChartData = (response: Record<string, any>) => {
    const latestDataPoint = response.data[response.data.length - 1];
    const latestDifferenceInListVsSoldPrice = (((latestDataPoint.soldAvg / latestDataPoint.priceAvg) * 100) - 100).toFixed(2);
    const formattedDifferenceInListVsSoldPrice = Number(latestDifferenceInListVsSoldPrice) > 0 ? `+${latestDifferenceInListVsSoldPrice}` : latestDifferenceInListVsSoldPrice;
    setCityAverageHomePrice(formattedDifferenceInListVsSoldPrice);

    const formattedChartData = response.data.map((r: Record<string, unknown>) => {
      const averageSoldPrice = r.soldAvg as number;
      return {
        x: r.soldAt,
        y: averageSoldPrice,
      } as GraphData;
    }).filter((data: Record<string, unknown>) => data.x && data.y);

    const oneMonthAgo = getDataAtPointInTime(response.data, 1, 'month');
    const threeMonthAgo = getDataAtPointInTime(response.data, 3, 'month');
    const sixMonthAgo = getDataAtPointInTime(response.data, 6, 'month');

    const listPriceChartData = response.data.map((r: Record<string, unknown>) => {
      const averageSoldPrice = r.priceAvg as number;
      return {
        x: r.soldAt,
        y: averageSoldPrice,
      } as GraphData;
    });//.filter((data: Record<string, unknown>) => data.x && data.y);
    setListPriceChartData(listPriceChartData);

    setOneMonthChange(Math.round(oneMonthAgo.soldAvg / oneMonthAgo.priceAvg * 100));
    setThreeMonthChange(Math.round(threeMonthAgo.soldAvg / threeMonthAgo.priceAvg * 100));
    setSixMonthChange(Math.round(sixMonthAgo.soldAvg / sixMonthAgo.priceAvg * 100));
    setCityData(formattedChartData);
  };

  useEffect(() => {
    if (chartData) {
      setChartData(chartData);
      setHasData(true);
    } else {
      setHasData(false);
    }
  }, [chartData]);

  return (
    <div className={styles.component}>
      {hasData &&
        <>
          <TopSectionStats
            isBlurred={isBlurred}
            chartTitle={'List vs Sold Price'}
            chartDescription="Initial listed price vs what it actually sold for"
            cityName={listing.city!}
            cityStat={`${cityAverageHomePrice!}%`}
            hideYoYStat={true}
            hideNeighbourhoodYoYStats={true}
            cityYoYStat={''}
            neighbourhoodName={listing.neighbourhoodName!}
            neighbourhoodStat={'-'}
            neighbourhoodYoYStat={'-'}
          />
          <div className={styles['list-vs-sold']} />
          <section className={styles['chart-container']}>
            <VictoryChart>
              <VictoryLegend
                x={35}
                y={-10}
                style={{
                  labels: {
                    fontSize: 20,
                    marginBottom: 40,
                  },
                }}
                orientation="horizontal"
                colorScale={[isExpTheme ? '#1B489B' : '#4695c4', isExpTheme ? '#6D071A' : '#c6483a']}
                data={[
                  { name: 'List Price' }, { name: 'Sold Price' },
                ]}
              />
              <VictoryLine
                interpolation="natural"
                data={cityData}
                style={isExpTheme ? soldPricelineStyleExp : soldPricelineStyle}
              />
              <VictoryLine
                interpolation="natural"
                data={listPriceChartData}
                style={isExpTheme ? listPricelineStyleExp : listPricelineStyle}
              />
              <VictoryAxis
                dependentAxis
                tickCount={3}
                fixLabelOverlap={true}
                tickFormat={t => abbreviateNumber(t)}
                style={isExpTheme ? axisStyleExp : axisStyle}
              />
              <VictoryAxis
                fixLabelOverlap={true}
                tickCount={3}
                style={isExpTheme ? axisStyleExp : axisStyle}
                tickFormat={t => dayjs(t).utc().format("MMM 'YY")}
              />
            </VictoryChart>
            {isBlurred && <div className={styles['blurred-chart']} />}
          </section>
          <div className={buildClassName(styles['data-summary'], styles['top-margin'])}>
            <div></div>
            <div className={styles.location}>
              {listing.city}
            </div>
          </div>
          <ChartStatTableRow label='1 Month Ago' isBlurred={isBlurred} city={oneMonthChange} />
          <ChartStatTableRow label='3 Months Ago' isBlurred={isBlurred} city={threeMonthChange} />
          <ChartStatTableRow label='6 Months Ago' isBlurred={isBlurred} city={sixMonthChange} />
        </>
      }
      {!hasData && <div className={styles.empty}><EmptyState message={'Not Enough Data'} /></div>}
    </div>
  );
}
